/**
 * EIR portfolio dashboard. For EIRs it shows their own assigned founders; for
 * admins it adds an EIR picker (getEIRList + getEIRFounders) to view any EIR's
 * portfolio. Each founder card summarizes data-room, fundraising, and CRM
 * status and exposes per-founder actions (email, password reset, engagement
 * status).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getMyFounders, getEIRList, getEIRFounders, eirResetPassword, setFounderEngagementStatus, getFounderDataRoom } from '../services/apiService';
import SlidePanel from '../components/SlidePanel';
import NotesTab from '../components/NotesTab';

// Section keys shared by the read-only card buttons and the detail popup.
const SECTION_LABELS = { data_room: 'Data Room', fundraising: 'Fundraising', crm: 'Investor Hygiene' };

/** Founder portfolio grid for an EIR (or an admin viewing a chosen EIR). */
export default function EIRDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [founders, setFounders] = useState([]);
  const [loading, setLoading] = useState(true);

  // EIR scope toggle: "mine" (assigned founders) vs "all" (program-wide, read-only)
  const [scope, setScope] = useState('mine');

  // Section-specific in-place popup ({ founder, section }) for cross-EIR
  // founders, whose deep pages the viewer can't open.
  const [detail, setDetail] = useState(null);

  // Admin EIR selector
  const [eirs, setEirs] = useState([]);
  const [selectedEirId, setSelectedEirId] = useState('');


  useEffect(() => {
    if (isAdmin) {
      getEIRList().then(setEirs).finally(() => setLoading(false));
    } else {
      setLoading(true);
      getMyFounders(scope).then(setFounders).finally(() => setLoading(false));
    }
  }, [isAdmin, scope]);

  useEffect(() => {
    if (!isAdmin || !selectedEirId) return;
    setLoading(true);
    getEIRFounders(parseInt(selectedEirId, 10))
      .then(setFounders)
      .finally(() => setLoading(false));
  }, [isAdmin, selectedEirId]);

  const selectedEir = eirs.find((e) => e.id === parseInt(selectedEirId, 10));

  const avgProgress = founders.length && founders[0]?.data_room
    ? Math.round(founders.reduce((s, f) => s + (f.data_room?.progress || 0), 0) / founders.length)
    : null;
  const plansCount   = founders.filter((f) => f.fundraising).length;
  const totalOverdue = founders.reduce((s, f) => s + (f.crm?.overdue || 0), 0);

  return (
    <div style={styles.page}>
      <div style={styles.inner}>

        <div style={styles.headerRow}>
          <div>
            <h1 style={styles.title}>{!isAdmin && scope === 'all' ? 'All Founders' : 'My Founders'}</h1>
            <p style={styles.subtitle}>
              {isAdmin
                ? selectedEir
                  ? `Viewing ${selectedEir.first_name} ${selectedEir.last_name}'s portfolio.`
                  : 'Select an EIR to view their portfolio.'
                : scope === 'all'
                  ? 'Every founder in the program. Founders not assigned to you are view-only.'
                  : `Welcome back, ${user?.first_name}. You have ${founders.length} founder${founders.length !== 1 ? 's' : ''} in your portfolio.`}
            </p>
          </div>
          {!isAdmin && (
            <div style={styles.scopeToggle}>
              <button
                style={{ ...styles.scopeBtn, ...(scope === 'mine' ? styles.scopeBtnActive : {}) }}
                onClick={() => setScope('mine')}
              >
                My Founders
              </button>
              <button
                style={{ ...styles.scopeBtn, ...(scope === 'all' ? styles.scopeBtnActive : {}) }}
                onClick={() => setScope('all')}
              >
                All Founders
              </button>
            </div>
          )}
          {isAdmin && (
            <div style={styles.eirSelector}>
              <label style={styles.selectorLabel}>Viewing as</label>
              <select
                style={styles.selectorInput}
                value={selectedEirId}
                onChange={(e) => setSelectedEirId(e.target.value)}
              >
                <option value="">— Select EIR —</option>
                {eirs.map((eir) => (
                  <option key={eir.id} value={eir.id}>
                    {eir.first_name} {eir.last_name} ({eir.founder_count} founder{eir.founder_count !== 1 ? 's' : ''})
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Stats row — EIR view only */}
        {!isAdmin && !loading && founders.length > 0 && avgProgress !== null && (
          <div style={styles.statsRow}>
            <StatCard label="Founders" value={founders.length} />
            <StatCard label="Avg. Data Room" value={`${avgProgress}%`} />
            <StatCard label="Plans Submitted" value={plansCount} />
            <StatCard label="Overdue Follow-ups" value={totalOverdue} />
          </div>
        )}

        {/* Founder grid */}
        {(selectedEirId || !isAdmin) && (
          <div>
            {loading && <p style={styles.dim}>Loading founders…</p>}
            {!loading && founders.length === 0 && (
              <p style={styles.dim}>No founders assigned yet.</p>
            )}
            {!loading && founders.length > 0 && (
              <div style={styles.grid}>
                {founders.map((f) => (
                  <FounderCard
                    key={f.id}
                    founder={f}
                    readOnly={!isAdmin && f.assigned === false}
                    onOpenSection={(section) => setDetail({ founder: f, section })}
                    onViewDataRoom={() => navigate('/eir/data-room', { state: { openFounderId: f.id } })}
                    onViewFundraising={() => navigate('/eir/fundraising', { state: { openFounderId: f.id } })}
                    onViewCRM={() => navigate('/eir/crm', { state: { openFounderId: f.id } })}
                  />
                ))}
              </div>
            )}
          </div>
        )}

      </div>

      <SlidePanel
        open={!!detail}
        onClose={() => setDetail(null)}
        title={detail ? `${detail.founder.first_name} ${detail.founder.last_name} · ${SECTION_LABELS[detail.section]}` : ''}
      >
        {detail && <SectionDetailPanel founder={detail.founder} section={detail.section} />}
      </SlidePanel>

    </div>
  );
}

// Section-specific popup for a cross-EIR founder: a read-only summary of the
// chosen section plus that section's notes thread, so another EIR can comment
// directly on that page. Fundraising/CRM detail stay owner-only (server 403s),
// so those show the card-level summary; the data room is viewable read-only.
function SectionDetailPanel({ founder, section }) {
  const [dataRoom, setDataRoom] = useState(null);
  const [loadingDr, setLoadingDr] = useState(section === 'data_room');

  useEffect(() => {
    if (section !== 'data_room') return;
    setLoadingDr(true);
    getFounderDataRoom(founder.id)
      .then(setDataRoom)
      .catch(() => setDataRoom(null))
      .finally(() => setLoadingDr(false));
  }, [founder.id, section]);

  const plan = founder.fundraising;
  const crm = founder.crm;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        {founder.company_name && <p style={detailStyles.company}>{founder.company_name}</p>}
        {founder.assigned_eir_name && (
          <p style={detailStyles.assigned}>Assigned EIR: <strong>{founder.assigned_eir_name}</strong> · you're commenting as a guest</p>
        )}
      </div>

      {/* Section summary */}
      {section === 'data_room' && (
        <div style={detailStyles.section}>
          {loadingDr && <p style={detailStyles.dim}>Loading data room…</p>}
          {!loadingDr && dataRoom && (
            <>
              <div style={detailStyles.summaryItem}>
                <span style={detailStyles.summaryLabel}>Overall</span>
                <span style={detailStyles.summaryValue}>{dataRoom.completedItems}/{dataRoom.totalItems} complete · {dataRoom.overallProgress}%</span>
              </div>
              {(dataRoom.categories || []).map((cat) => (
                <div key={cat.id} style={detailStyles.summaryItem}>
                  <span style={detailStyles.catName}>{cat.name}</span>
                  <span style={detailStyles.summaryValue}>{cat.completedItems}/{cat.totalItems}</span>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {section === 'fundraising' && (
        <div style={detailStyles.section}>
          <div style={detailStyles.summaryItem}>
            <span style={detailStyles.summaryLabel}>Plan</span>
            <span style={detailStyles.summaryValue}>
              {plan ? [plan.raise_amount ? `${fmtAmount(plan.raise_amount)} raise` : null,
                       plan.min_check_size ? `${fmtAmount(plan.min_check_size)} min` : null].filter(Boolean).join(' · ') || 'Submitted'
                    : 'No plan submitted'}
            </span>
          </div>
          <p style={detailStyles.note}>Full plan detail is visible to the assigned EIR only.</p>
        </div>
      )}

      {section === 'crm' && (
        <div style={detailStyles.section}>
          <div style={detailStyles.summaryItem}>
            <span style={detailStyles.summaryLabel}>Investor Hygiene</span>
            <span style={detailStyles.summaryValue}>
              {crm ? `${crm.total} contact${crm.total !== 1 ? 's' : ''}${crm.overdue ? ` · ${crm.overdue} overdue` : ''}` : '—'}
            </span>
          </div>
          <p style={detailStyles.note}>Full contact list is visible to the assigned EIR only.</p>
        </div>
      )}

      <div style={detailStyles.divider} />

      <p style={detailStyles.notesHeading}>{SECTION_LABELS[section]} notes</p>
      <NotesTab
        founderUserId={founder.id}
        founderName={`${founder.first_name} ${founder.last_name}`}
        page={section}
      />
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div style={styles.statCard}>
      <p style={styles.statValue}>{value}</p>
      <p style={styles.statLabel}>{label}</p>
    </div>
  );
}

// Per-founder summary card. Reset Password returns a one-time temp password to
// hand to the founder (who must change it on next login); engagement status is
// saved immediately on change.
function FounderCard({ founder, readOnly = false, onOpenSection, onViewDataRoom, onViewFundraising, onViewCRM }) {
  const dr   = founder.data_room;
  const plan = founder.fundraising;
  const crm  = founder.crm;

  const [resetting, setResetting] = useState(false);
  const [tempPassword, setTempPassword] = useState(null);
  const [copied, setCopied] = useState(false);
  const [engagementStatus, setEngagementStatus] = useState(founder.engagement_status || 'active');
  const [savingEngagement, setSavingEngagement] = useState(false);

  async function handleEngagementChange(e) {
    const val = e.target.value;
    setSavingEngagement(true);
    try {
      await setFounderEngagementStatus(founder.id, val);
      setEngagementStatus(val);
    } finally { setSavingEngagement(false); }
  }

  async function handleResetPassword() {
    setResetting(true);
    setTempPassword(null);
    try {
      const result = await eirResetPassword(founder.id);
      setTempPassword(result.temp_password);
    } finally {
      setResetting(false);
    }
  }

  function handleCopy() {
    navigator.clipboard.writeText(tempPassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div style={styles.card}>
      <div style={styles.cardTop}>
        <div style={styles.cardNameRow}>
          <p style={styles.cardName}>{founder.first_name} {founder.last_name}</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            {founder.company_stage && (
              <span style={{ ...styles.stagePill, ...stageColor(founder.company_stage) }}>
                {founder.company_stage}
              </span>
            )}
            {readOnly ? (
              <span style={styles.assignedBadge} title="Assigned EIR">
                {founder.assigned_eir_name ? `Assigned: ${founder.assigned_eir_name}` : 'Unassigned'}
              </span>
            ) : (
              <select
                style={{
                  ...styles.engagementSelect,
                  background: engagementStatus === 'active' ? '#e6f4ea' : engagementStatus === 'dormant' ? '#fff8e1' : '#f5f5f5',
                  color: engagementStatus === 'active' ? '#1e6b3a' : engagementStatus === 'dormant' ? '#7a5c00' : '#555',
                }}
                value={engagementStatus}
                onChange={handleEngagementChange}
                disabled={savingEngagement}
              >
                <option value="active">Active</option>
                <option value="dormant">Dormant</option>
                <option value="archived">Archived</option>
              </select>
            )}
          </div>
        </div>
        {founder.company_name && (
          <p style={styles.cardCompany}>{founder.company_name}</p>
        )}
        {founder.industry && (
          <p style={styles.cardMeta}>{founder.industry}{founder.location ? ` · ${founder.location}` : ''}</p>
        )}
      </div>

      <div style={styles.cardDivider} />

      {/* Data room progress */}
      {dr && (
        <div style={styles.cardSection}>
          <div style={styles.cardSectionHeader}>
            <span style={styles.cardSectionLabel}>Data Room</span>
            <span style={styles.cardSectionValue}>{(dr.completed_count ?? dr.uploaded_count)}/{dr.total_count} complete</span>
          </div>
          <div style={styles.progressTrack}>
            <div style={{ ...styles.progressFill, width: `${dr.progress}%` }} />
          </div>
          <p style={styles.progressPct}>{dr.progress}% complete</p>
        </div>
      )}

      {/* Fundraising summary */}
      <div style={styles.cardSection}>
        <span style={styles.cardSectionLabel}>Fundraising Plan</span>
        {plan ? (
          <p style={styles.planLine}>
            {plan.raise_amount ? `${fmtAmount(plan.raise_amount)} raise` : ''}
            {plan.raise_amount && plan.min_check_size ? ' · ' : ''}
            {plan.min_check_size ? `${fmtAmount(plan.min_check_size)} min check` : ''}
          </p>
        ) : (
          <p style={styles.planNone}>No plan submitted</p>
        )}
      </div>

      {/* CRM summary */}
      <div style={styles.cardSection}>
        <div style={styles.cardSectionHeader}>
          <span style={styles.cardSectionLabel}>Investor Hygiene</span>
          {crm?.overdue > 0 && (
            <span style={styles.overdueTag}>{crm.overdue} overdue</span>
          )}
        </div>
        {crm ? (
          <p style={styles.crmLine}>
            {crm.total} contact{crm.total !== 1 ? 's' : ''}
            {crm.last_activity ? ` · last activity ${crm.last_activity}` : ''}
          </p>
        ) : (
          <p style={styles.planNone}>No contacts yet</p>
        )}
      </div>

      <div style={styles.cardActions}>
        {readOnly ? (
          // Cross-EIR founder: same three sections as an owned founder, but each
          // opens an in-place section popup (deep pages can't load someone
          // else's founder) where a guest EIR can read and comment on that page.
          <>
            <button style={styles.actionBtn} onClick={() => onOpenSection('data_room')}>Data Room</button>
            <button style={styles.actionBtn} onClick={() => onOpenSection('fundraising')}>Fundraising</button>
            <button style={styles.actionBtn} onClick={() => onOpenSection('crm')}>Investor Hygiene</button>
          </>
        ) : (
          <>
            <button style={styles.actionBtn} onClick={onViewDataRoom}>Data Room</button>
            <button style={styles.actionBtn} onClick={onViewFundraising}>Fundraising</button>
            <button style={styles.actionBtn} onClick={onViewCRM}>Investor Hygiene</button>
          </>
        )}
      </div>

      <div style={styles.cardActionsSecondary}>
        <a
          href={`mailto:${founder.email}?subject=${encodeURIComponent(`Re: ${founder.company_name || founder.first_name + ' ' + founder.last_name}`)}`}
          style={styles.emailBtn}
        >
          Email Founder
        </a>
        {!readOnly && (
          <button style={styles.resetBtn} onClick={handleResetPassword} disabled={resetting}>
            {resetting ? 'Resetting…' : 'Reset Password'}
          </button>
        )}
      </div>

      {tempPassword && (
        <div style={styles.tempBox}>
          <p style={styles.tempLabel}>Share this temporary password with the founder:</p>
          <div style={styles.tempRow}>
            <code style={styles.tempCode}>{tempPassword}</code>
            <button style={styles.copyBtn} onClick={handleCopy}>{copied ? 'Copied!' : 'Copy'}</button>
          </div>
          <p style={styles.tempNote}>They will be required to set a new password on next login.</p>
        </div>
      )}
    </div>
  );
}

// Compact money formatter: $X.XM for millions, otherwise $XK.
function fmtAmount(n) {
  if (!n) return '';
  if (n >= 1_000_000) return `$${(n / 1_000_000 % 1 === 0 ? n / 1_000_000 : (n / 1_000_000).toFixed(1))}M`;
  return `$${Math.round(n / 1_000)}K`;
}

const detailStyles = {
  company: { fontFamily: 'var(--font-body)', fontSize: 18, fontWeight: 700, color: 'var(--color-forest)' },
  assigned: { fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-forest)', opacity: 0.7, marginTop: 4 },
  section: { display: 'flex', flexDirection: 'column', gap: 8 },
  summaryItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, borderBottom: '1px solid #f0f0f0', paddingBottom: 6 },
  summaryLabel: { fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 800, color: 'var(--color-forest)', opacity: 0.55, textTransform: 'uppercase', letterSpacing: '0.05em' },
  catName: { fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--color-forest)', opacity: 0.8 },
  summaryValue: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', textAlign: 'right' },
  dim: { fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--color-forest)', opacity: 0.5, fontStyle: 'italic' },
  note: { fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--color-forest)', opacity: 0.5, fontStyle: 'italic', marginTop: 2 },
  divider: { height: 1, background: '#f0f0f0' },
  notesHeading: { fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 800, color: 'var(--color-forest)' },
};

function stageColor(stage) {
  const map = {
    idea:      { background: '#f0e8ff', color: '#5b21b6' },
    prototype: { background: '#fef9c3', color: '#854d0e' },
    pilot:     { background: '#e0f2fe', color: '#0369a1' },
    revenue:   { background: '#dcfce7', color: '#15803d' },
    scaling:   { background: '#fff7ed', color: '#c2410c' },
  };
  return map[stage] || { background: 'var(--color-mint-dark)', color: 'var(--color-forest)' };
}

const styles = {
  page: { flex: 1, overflowY: 'auto', background: 'linear-gradient(to bottom, var(--color-frost) 0%, var(--color-mint-dark) 100%) fixed', padding: 'var(--space-8)' },
  inner: { maxWidth: "98%", margin: "0 auto" },

  headerRow: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-6)', flexWrap: 'wrap', marginBottom: 'var(--space-6)', paddingTop: 'var(--space-4)' },
  title: { fontFamily: 'var(--font-accent)', fontSize: 80, fontWeight: 500, color: 'var(--color-rust)', lineHeight: 1.1, marginBottom: 'var(--space-2)', fontWeight: 800},
  subtitle: { fontFamily: 'var(--font-body)', fontSize: 30, fontWeight: 'var(--font-weight-page-subtitle)', color: 'var(--color-forest)', opacity: 0.6, lineHeight: 1.0 },

  scopeToggle: { display: 'flex', gap: 4, background: 'var(--color-frost)', border: '1.5px solid var(--color-mint-dark)', borderRadius: 'var(--radius-full)', padding: 3, height: 'fit-content', marginTop: 8 },
  scopeBtn: { fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 700, color: 'var(--color-forest)', background: 'none', border: 'none', borderRadius: 'var(--radius-full)', padding: '6px 16px', cursor: 'pointer', opacity: 0.6 },
  scopeBtnActive: { background: 'var(--color-forest)', color: '#fff', opacity: 1 },
  assignedBadge: { fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 700, color: 'var(--color-forest)', background: 'var(--color-mint)', border: '1.5px solid var(--color-mint-dark)', borderRadius: 'var(--radius-full)', padding: '2px 10px', whiteSpace: 'nowrap' },

  eirSelector: { display: 'flex', flexDirection: 'column', gap: 6, minWidth: 230 },
  selectorLabel: { fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 700, color: 'var(--color-forest)', opacity: 0.55, textTransform: 'uppercase', letterSpacing: '0.06em' },
  selectorInput: { padding: '8px 12px', borderRadius: 'var(--radius-sm)', border: '1.5px solid var(--color-mint-dark)', background: 'var(--color-frost)', fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)' },

  statsRow: { display: 'flex', gap: 'var(--space-4)', marginBottom: 'var(--space-6)', flexWrap: 'wrap', maxWidth: 800 },
  statCard: { background: 'var(--color-frost)', borderRadius: 'var(--radius-md)', padding: '16px 24px', boxShadow: 'var(--shadow-card)', flex: 1, textAlign: 'center' },
  statValue: { fontFamily: 'var(--font-accent)', fontSize: 28, fontWeight: 800, color: 'var(--color-forest)', lineHeight: 1.1, marginBottom: 4 },
  statLabel: { fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 700, color: 'var(--color-forest)', opacity: 0.55, textTransform: 'uppercase', letterSpacing: '0.06em' },

  dim: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', opacity: 0.5, fontStyle: 'italic' },

  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 420px))', gap: 'var(--space-5)' },

  card: { background: 'var(--color-frost)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-card)', padding: 'var(--space-5)', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' },
  cardTop: { display: 'flex', flexDirection: 'column', gap: 4 },
  cardNameRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  cardName: { fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-card-title)', fontWeight: 700, color: 'var(--color-forest)' },
  cardCompany: { fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 600, color: 'var(--color-forest)', opacity: 0.85 },
  cardMeta: { fontFamily: 'var(--font-body)', fontSize: 16, color: 'var(--color-forest)', opacity: 0.55 },

  stagePill: { padding: '2px 8px', borderRadius: 'var(--radius-full)', fontSize: 15, fontWeight: 700, flexShrink: 0, textTransform: 'capitalize' },
  engagementSelect: { fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 700, border: '1.5px solid transparent', borderRadius: 'var(--radius-full)', padding: '2px 8px', cursor: 'pointer' },

  cardDivider: { height: 1, background: 'var(--color-mint-dark)', margin: '2px 0' },

  cardSection: { display: 'flex', flexDirection: 'column', gap: 4 },
  cardSectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' },
  cardSectionLabel: { fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 700, color: 'var(--color-forest)', opacity: 0.55, textTransform: 'uppercase', letterSpacing: '0.05em' },
  cardSectionValue: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', opacity: 0.7 },

  progressTrack: { height: 6, background: 'var(--color-mint-dark)', borderRadius: 3, overflow: 'hidden' },
  progressFill: { height: '100%', background: 'var(--color-forest)', borderRadius: 3, transition: 'width 0.4s ease' },
  progressPct: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', opacity: 0.6 },

  planLine:    { fontFamily: 'var(--font-body)', fontSize: 16, color: 'var(--color-forest)', fontWeight: 600 },
  planNone:    { fontFamily: 'var(--font-body)', fontSize: 16, color: 'var(--color-forest)', opacity: 0.45, fontStyle: 'italic' },
  crmLine:     { fontFamily: 'var(--font-body)', fontSize: 16, color: 'var(--color-forest)', fontWeight: 600 },
  overdueTag:  { fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 700, color: '#dc2626', background: '#fee2e2', borderRadius: 'var(--radius-full)', padding: '2px 8px' },

  cardActions: { display: 'flex', gap: 8, marginTop: 4 },
  actionBtn: { flex: 1, padding: '7px 0', borderRadius: 'var(--radius-sm)', fontSize: 16, fontWeight: 600, fontFamily: 'var(--font-body)', color: 'var(--color-forest)', background: 'var(--color-mint)', border: '1.5px solid var(--color-mint-dark)', cursor: 'pointer' },

  cardActionsSecondary: { display: 'flex', gap: 8 },
  emailBtn: { fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: '#1a5276', background: '#e8f4fd', border: '1.5px solid #aed6f1', borderRadius: 'var(--radius-sm)', padding: '6px 14px', textDecoration: 'none', display: 'inline-flex', alignItems: 'center' },
  resetBtn: { fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: '#1a5276', background: '#e8f4fd', border: '1.5px solid #aed6f1', borderRadius: 'var(--radius-sm)', padding: '6px 14px', cursor: 'pointer' },
  tempBox: { padding: '12px 14px', background: '#e8f4fd', borderRadius: 'var(--radius-sm)', border: '1.5px solid #aed6f1' },
  tempLabel: { fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600, color: '#1a5276', marginBottom: 8 },
  tempRow: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 },
  tempCode: { fontFamily: 'monospace', fontSize: 14, fontWeight: 700, color: '#1a5276', letterSpacing: '0.05em', flex: 1 },
  copyBtn: { fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600, color: '#1a5276', background: '#fff', border: '1.5px solid #aed6f1', borderRadius: 'var(--radius-sm)', padding: '4px 10px', cursor: 'pointer' },
  tempNote: { fontFamily: 'var(--font-body)', fontSize: 11, color: '#1a5276', opacity: 0.75, margin: 0 },
};
